import log from '@/lib/logger';
import { useState } from 'react';
import { Button } from '@/components/tailwind/button';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/tailwind/dialog';
import { Field, Label } from '@/components/tailwind/fieldset';
import { Textarea } from '@/components/tailwind/textarea';
import { ErrorMessage } from '@/components/tailwind/fieldset';
import { useCommonTranslation, useOrdersTranslation } from '@/i18n/hooks';
import { reopenOrder } from '@/services/order-services';
import { useAuth } from '@/context/use-auth';
import { useToast } from '@/hooks/use-toast';

interface ReopenOrderDialogProps {
    orderId: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function ReopenOrderDialog({ orderId, isOpen, onClose, onSuccess }: ReopenOrderDialogProps) {
    const { t } = useOrdersTranslation();
    const { t: tCommon } = useCommonTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    function handleClose() {
        setReason('');
        setError('');
        onClose();
    }

    async function handleSubmit() {
        if (!reason.trim()) {
            setError(t('reopen.reasonRequired'));
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await reopenOrder(token, orderId, reason);
            toast({
                title: t('reopen.success'),
                variant: 'default',
            });
            handleClose();
            onSuccess();
        } catch (error) {
            log.error('Failed to reopen order:', error);
            setError(t('reopen.error'));
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle>{t('reopen.title')}</DialogTitle>
            <DialogDescription>{t('reopen.description')}</DialogDescription>
            <DialogBody>
                <Field>
                    <Label>{t('reopen.reasonLabel')}</Label>
                    <Textarea
                        name="reason"
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t('reopen.reasonPlaceholder')}
                        invalid={!!error}
                        data-testid="order-reopen-reason"
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Field>
            </DialogBody>
            <DialogActions>
                <Button plain onClick={handleClose} disabled={isSubmitting}>
                    {tCommon('buttons.cancel.text')}
                </Button>
                <Button
                    color="amber"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    data-testid="order-reopen-submit-button"
                >
                    {isSubmitting ? tCommon('buttons.processing') : t('actions.reopen')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}